/**
 * Ledger money helpers — string/BigInt only, never Number for amounts.
 * Joule/WATT 默认 18 decimals（J18）；USDT/PANTA 默认 18。
 * 优先使用接口 `decimals` / runtime（platform-coin / 响应头）覆盖。
 *
 * @see docs/joule-j18-migration.md
 */

/** J18：1 Joule = 10^18 最小单位 */
export const WATT_DECIMALS = 18;
/** 与 WATT_DECIMALS 同义（文档称 Joule） */
export const JOULE_LEDGER_DECIMALS = WATT_DECIMALS;
export const USDT_DECIMALS = 18;
/** PANTA / 普通保险 U 价值：18 位最小单位 */
export const PANTA_DECIMALS = 18;

/** 运行时 Joule 精度（来自 platform-coin / 响应头）；未同步前用 J18 默认 */
let runtimeJouleDecimals: number | null = null;

export function setRuntimeJouleDecimals(decimals: number | null | undefined): void {
  if (decimals == null) return;
  const n = Number(decimals);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 36) return;
  runtimeJouleDecimals = n;
}

/** 当前环境 Joule 账本精度（写请求头与换算共用） */
export function getJouleDecimals(): number {
  return runtimeJouleDecimals ?? WATT_DECIMALS;
}

export function isJouleJ18(): boolean {
  return getJouleDecimals() === 18;
}

const DIGITS_ONLY = /^\d+$/;

function assertNonEmpty(value: string, label: string): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    throw new Error(`${label} is required`);
  }
  return trimmed;
}

function scaleFactor(decimals: number): bigint {
  if (!Number.isInteger(decimals) || decimals < 0) {
    throw new Error(`Invalid decimals: ${decimals}`);
  }
  return 10n ** BigInt(decimals);
}

/**
 * Convert a human decimal amount (e.g. "10.5") to a min-unit integer string.
 */
function toMinUnit(amount: string, decimals: number): string {
  const raw = assertNonEmpty(amount, 'amount');
  if (raw.startsWith('-')) {
    throw new Error('Negative amounts are not allowed');
  }

  const normalized = raw.replace(/^0+(?=\d)/, '') || '0';
  const [wholePart, fracPart = ''] = normalized.split('.');

  if (normalized.split('.').length > 2) {
    throw new Error(`Invalid amount: ${amount}`);
  }
  if (!DIGITS_ONLY.test(wholePart || '0') || (fracPart && !DIGITS_ONLY.test(fracPart))) {
    throw new Error(`Invalid amount: ${amount}`);
  }
  if (fracPart.length > decimals) {
    throw new Error(`Amount has more than ${decimals} decimal places: ${amount}`);
  }

  const whole = BigInt(wholePart || '0');
  const fracPadded = (fracPart + '0'.repeat(decimals)).slice(0, decimals);
  const frac = fracPadded ? BigInt(fracPadded) : 0n;
  return (whole * scaleFactor(decimals) + frac).toString();
}

/**
 * Convert a min-unit integer string to a human decimal string (no scientific notation).
 * Trailing zeros after the decimal point are trimmed; at least one fraction digit is kept only when needed.
 */
export function fromMinUnit(minUnit: string, decimals: number): string {
  const raw = assertNonEmpty(minUnit, 'minUnit');
  if (raw.startsWith('-')) {
    throw new Error('Negative amounts are not allowed');
  }
  if (!DIGITS_ONLY.test(raw)) {
    throw new Error(`Invalid min-unit amount: ${minUnit}`);
  }

  const value = BigInt(raw);
  const base = scaleFactor(decimals);
  const whole = value / base;
  const frac = value % base;

  if (frac === 0n) {
    return whole.toString();
  }

  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole.toString()}.${fracStr}`;
}

function resolveDecimals(decimals: number | null | undefined, fallback: number): number {
  // Number(null) === 0，不能把 null 当成 0 位小数
  if (decimals == null || decimals === ('' as unknown)) return fallback;
  const n = Number(decimals);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 36) return fallback;
  return n;
}

function resolveJouleDecimals(decimals?: number | null): number {
  return resolveDecimals(decimals, getJouleDecimals());
}

/** 1 Joule → J18 最小单位字符串（如 "1000000000000000000"） */
export function wattToMinUnit(amount: string, decimals?: number | null): string {
  return toMinUnit(amount, resolveJouleDecimals(decimals));
}

/** Joule 最小单位 → 可读十进制；可传入接口 decimals */
export function minUnitToWatt(str: string, decimals?: number | null): string {
  return fromMinUnit(str, resolveJouleDecimals(decimals));
}

/** 1 USDT → "1000000000000000000" */
export function usdtToMinUnit(amount: string, decimals?: number | null): string {
  return toMinUnit(amount, resolveDecimals(decimals, USDT_DECIMALS));
}

/** "1000000000000000000" → "1"；decimals 为空时回退 18 */
export function minUnitToUsdt(str: string, decimals?: number | null): string {
  return fromMinUnit(str, resolveDecimals(decimals, USDT_DECIMALS));
}

/** 1 PANTA → 18 位最小单位 */
export function pantaToMinUnit(amount: string, decimals?: number | null): string {
  return toMinUnit(amount, resolveDecimals(decimals, PANTA_DECIMALS));
}

/** PANTA18 / U18 最小单位 → 可读十进制 */
export function minUnitToPanta(str: string, decimals?: number | null): string {
  return fromMinUnit(str, resolveDecimals(decimals, PANTA_DECIMALS));
}

/**
 * 有符号最小单位 → 可读十进制（收益可为负）。
 * 禁止先 Number() 再格式化。
 */
export function fromMinUnitSigned(
  minUnit: string,
  decimals: number | null | undefined,
  fallbackDecimals = WATT_DECIMALS
): string {
  const raw = String(minUnit ?? '0').trim() || '0';
  const neg = raw.startsWith('-');
  const abs = neg ? raw.slice(1) : raw;
  try {
    const h = fromMinUnit(abs || '0', resolveDecimals(decimals, fallbackDecimals));
    if (h === '0') return '0';
    return neg ? `-${h}` : h;
  } catch {
    return '0';
  }
}

export function minUnitToWattSigned(
  str: string,
  decimals?: number | null
): string {
  return fromMinUnitSigned(str, decimals, getJouleDecimals());
}

export function minUnitToPantaSigned(
  str: string,
  decimals?: number | null
): string {
  return fromMinUnitSigned(str, decimals, PANTA_DECIMALS);
}

/**
 * 同精度人类可读小数相加（禁止 Number 浮点资金运算）。
 * 例如 "1.5" + "2.25" → "3.75"
 */
export function addDecimalStrings(a: string, b: string): string {
  const left = String(a ?? '0').trim() || '0';
  const right = String(b ?? '0').trim() || '0';
  const leftNeg = left.startsWith('-');
  const rightNeg = right.startsWith('-');
  if (leftNeg || rightNeg) {
    throw new Error('Negative decimal add not supported');
  }
  const [lw, lf = ''] = left.split('.');
  const [rw, rf = ''] = right.split('.');
  const scale = Math.max(lf.length, rf.length);
  const lMin = BigInt(lw || '0') * 10n ** BigInt(scale) + BigInt((lf + '0'.repeat(scale)).slice(0, scale) || '0');
  const rMin = BigInt(rw || '0') * 10n ** BigInt(scale) + BigInt((rf + '0'.repeat(scale)).slice(0, scale) || '0');
  const sum = lMin + rMin;
  if (scale === 0) return sum.toString();
  const base = 10n ** BigInt(scale);
  const whole = sum / base;
  const frac = sum % base;
  if (frac === 0n) return whole.toString();
  return `${whole.toString()}.${frac.toString().padStart(scale, '0').replace(/0+$/, '')}`;
}

/** 别名：U18 权益字段与 USDT 同精度 */
export const minUnitToUsdt18 = minUnitToPanta;
export const usdt18ToMinUnit = pantaToMinUnit;
